/**
 * テーマ管理フック
 *
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business dark/light/system の 3 モードを localStorage 永続化と OS 設定連動で管理する共有テーマフック。storageKey をアプリごとに受け取り、3 web アプリで実装を共有する。
 * @graph-connects none
 */
import { useState, useEffect, useCallback } from 'react';

/** テーマ設定値。system は OS 設定に追従する。 */
export type Theme = 'dark' | 'light' | 'system';

/** 実際に適用されるテーマ（system を解決した結果）。 */
export type ResolvedTheme = 'dark' | 'light';

/**
 * OS のカラースキーム設定から解決済みテーマを取得する。
 *
 * @returns OS が light を要求していれば 'light'、それ以外は 'dark'
 * @graph-connects none
 */
function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * テーマ設定値を解決済みテーマへ変換する。
 *
 * @param theme - テーマ設定値
 * @returns 解決済みテーマ
 * @graph-connects none
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}

/**
 * 解決済みテーマを html 要素の data-theme 属性へ反映する。
 *
 * @param resolved - 解決済みテーマ
 * @graph-connects none
 */
function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.setAttribute('data-theme', resolved);
}

/**
 * テーマ管理フック。dark/light/system の 3 モードに対応し、選択値を
 * localStorage（storageKey）へ永続化、system モード時は OS 設定変更へ追従する。
 *
 * @param storageKey - テーマ選択値を保存する localStorage キー（アプリごとに指定）
 * @returns theme（設定値）、resolved（解決済み）、setTheme（更新関数）
 * @graph-connects none
 */
export function useTheme(storageKey: string): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (next: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey);
    return stored === 'dark' || stored === 'light' || stored === 'system' ? stored : 'dark';
  });

  const resolved = resolveTheme(theme);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      localStorage.setItem(storageKey, next);
      applyTheme(resolveTheme(next));
    },
    [storageKey],
  );

  // OS 設定変更の監視（system モード時）
  useEffect(() => {
    applyTheme(resolved);

    if (theme !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (): void => {
      applyTheme(getSystemTheme());
    };
    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, [theme, resolved]);

  return { theme, resolved, setTheme };
}
